import { buildSearchText } from "@nomnom/core";
import type { Order, OrderStatus, User } from "@nomnom/core";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

type HallMap = Record<string, string>;
type UserMap = Record<string, User>;

export type OrderRow = {
  id: string;
  status: OrderStatus;
  studentName: string;
  studentEmail: string;
  riderName: string;
  diningHall: string;
  amount: number;
  tip: number;
  createdAt: Order["createdAt"] | undefined;
  notes: string;
};

export function subscribeToOrders(
  onUpdate: (orders: Order[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(250)),
    (snapshot) => {
      onUpdate(
        snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as Order,
        ),
      );
    },
    (error) => {
      console.error("[OrdersService] Failed to load orders:", error);
      onError("Failed to load orders.");
    },
  );
}

export function subscribeToUsers(
  onUpdate: (users: User[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, "users"),
    (snapshot) => {
      onUpdate(
        snapshot.docs.map(
          (docSnap) => ({ uid: docSnap.id, ...docSnap.data() }) as User,
        ),
      );
    },
    (error) => {
      console.error("[OrdersService] Failed to load users:", error);
      onError("Failed to load users for order lookup.");
    },
  );
}

export function subscribeToOrderDiningHalls(
  onUpdate: (diningHalls: HallMap) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, "diningHalls"),
    (snapshot) => {
      const hallMap: HallMap = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() as { name?: string };
        hallMap[docSnap.id] = data.name || docSnap.id;
      });
      onUpdate(hallMap);
    },
    (error) => {
      console.error("[OrdersService] Failed to load dining halls:", error);
      onError("Failed to load dining hall metadata.");
    },
  );
}

export function buildUsersById(users: User[]): UserMap {
  return users.reduce<UserMap>((accumulator, user) => {
    accumulator[user.uid] = user;
    return accumulator;
  }, {});
}

export function buildOrderRows(
  orders: Order[],
  usersById: UserMap,
  diningHalls: HallMap,
): OrderRow[] {
  return orders.map((order) => {
    const student = usersById[order.customerUid];
    const rider = order.riderUid ? usersById[order.riderUid] : undefined;

    return {
      id: order.id,
      status: order.status,
      studentName: student?.displayName || "Unknown student",
      studentEmail: student?.email || "No email",
      riderName: rider?.displayName || "Unassigned",
      diningHall: diningHalls[order.diningHallRef] || order.diningHallRef,
      amount:
        typeof order.total === "number"
          ? order.total
          : (order.fee || 0) + (order.tip || 0),
      tip: order.tip || 0,
      createdAt: order.createdAt,
      notes: order.notes || "",
    };
  });
}

export function filterOrderRows(
  orderRows: OrderRow[],
  filter: "all" | OrderStatus,
  search: string,
): OrderRow[] {
  const normalizedSearch = search.trim().toLowerCase();

  return orderRows.filter((order) => {
    const matchesFilter = filter === "all" || order.status === filter;
    if (!matchesFilter) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return buildSearchText([
      order.id,
      order.studentName,
      order.studentEmail,
      order.riderName,
      order.diningHall,
      order.status,
    ]).includes(normalizedSearch);
  });
}

export async function cancelOrderById(
  orderId: string,
  cancelReason: string,
): Promise<void> {
  try {
    await updateDoc(doc(db, "orders", orderId), {
      status: "cancelled",
      cancelledAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      issue: cancelReason.trim()
        ? {
            type: "other",
            description: cancelReason.trim(),
            reportedAt: Timestamp.now(),
          }
        : null,
    });
  } catch (error) {
    console.error("[OrdersService] Failed to cancel order:", error);
    throw new Error("Failed to cancel the order.");
  }
}
